import type { WebsiteRichTextDocument } from '../../types/website-templates';

/**
 * C3 plain text ↔ website rich text, for the Admin rich-text field (a plain
 * textarea). The website grammar is link-free v1: paragraphs and lists of
 * text plus soft breaks.
 *
 * Written form:
 * - a blank line ends a block;
 * - a line break inside a paragraph is a soft break;
 * - lines that start with `- ` or `• ` are the items of an unordered list.
 *
 * This is the EDITING form. Template default copy keeps its own rule (one
 * paragraph per non-empty line): `websitePlainTextToRichText` in `resolve.ts`
 * and SQL `website_internal.website_plain_text_to_rich_text`.
 *
 * Nothing here validates: the result goes through `validateWebsiteRichText`
 * like any other value.
 */
type Block = WebsiteRichTextDocument['children'][number];
type Inline = Extract<Block, { readonly type: 'paragraph' }>['children'][number];

const BULLET_MARKERS = ['- ', '• '] as const;

function bulletText(line: string): string | null {
  for (const marker of BULLET_MARKERS) {
    if (line.startsWith(marker)) return line.slice(marker.length);
  }
  return null;
}

function paragraph(lines: readonly string[]): Block {
  const children: Inline[] = [];
  lines.forEach((line, index) => {
    if (index > 0) children.push({ type: 'softBreak' });
    children.push({ type: 'text', text: line });
  });
  return { type: 'paragraph', children };
}

function list(items: readonly string[]): Block {
  return {
    type: 'list',
    style: 'unordered',
    items: items.map((text) => ({ type: 'listItem', children: [{ type: 'text', text }] })),
  };
}

/** Text is kept exactly as typed, except that `\r\n` and `\r` become `\n`. */
export function plainTextToWebsiteRichText(text: string): WebsiteRichTextDocument {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const children: Block[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  const flush = (): void => {
    if (paragraphLines.length > 0) children.push(paragraph(paragraphLines));
    if (listItems.length > 0) children.push(list(listItems));
    paragraphLines = [];
    listItems = [];
  };

  for (const line of lines) {
    if (line.trim() === '') {
      flush();
      continue;
    }
    const item = bulletText(line);
    if (item !== null) {
      if (paragraphLines.length > 0) flush();
      listItems.push(item);
    } else {
      if (listItems.length > 0) flush();
      paragraphLines.push(line);
    }
  }
  flush();

  return { type: 'doc', version: 1, children };
}

interface PlainState {
  lossy: boolean;
}

function inlineText(nodes: readonly Inline[], softBreak: string, state: PlainState): string {
  let text = '';
  for (const node of nodes) {
    if (node.type === 'softBreak') {
      text += softBreak;
      continue;
    }
    // A link is outside the website grammar; its text is all that can be kept.
    if (node.type === 'link' || (node.marks !== undefined && node.marks.length > 0)) {
      state.lossy = true;
    }
    text += node.text;
  }
  return text;
}

function blockText(block: Block, state: PlainState): string {
  if (block.type === 'paragraph') return inlineText(block.children, '\n', state);
  if (block.style === 'ordered') state.lossy = true;
  return block.items
    .map((item, index) => {
      const marker = block.style === 'ordered' ? `${index + 1}. ` : BULLET_MARKERS[0];
      return `${marker}${inlineText(item.children, ' ', state)}`;
    })
    .join('\n');
}

/** Same document when adjacent plain text nodes are read as one. */
function canonical(doc: WebsiteRichTextDocument): string {
  const inlines = (nodes: readonly Inline[]): unknown[] => {
    const merged: unknown[] = [];
    let pending: string | null = null;
    for (const node of nodes) {
      if (node.type === 'text' && (node.marks === undefined || node.marks.length === 0)) {
        pending = (pending ?? '') + node.text;
        continue;
      }
      if (pending !== null) merged.push({ type: 'text', text: pending });
      pending = null;
      merged.push(node);
    }
    if (pending !== null) merged.push({ type: 'text', text: pending });
    return merged;
  };
  return JSON.stringify(
    doc.children.map((block) =>
      block.type === 'paragraph'
        ? { type: block.type, children: inlines(block.children) }
        : {
            type: block.type,
            style: block.style,
            items: block.items.map((item) => ({ children: inlines(item.children) })),
          },
    ),
  );
}

/**
 * `lossy` is true when writing the text back would not give the same
 * document: marks, ordered lists, links, a soft break inside a list item, or
 * text that reads as another block in the written form. The field shows a
 * notice then, and an untouched document is never rewritten (C3
 * `toWebsiteSlotBatch`).
 */
export function websiteRichTextToPlainText(doc: WebsiteRichTextDocument): {
  readonly text: string;
  readonly lossy: boolean;
} {
  const state: PlainState = { lossy: false };
  const text = doc.children.map((block) => blockText(block, state)).join('\n\n');
  const lossy = state.lossy || canonical(plainTextToWebsiteRichText(text)) !== canonical(doc);
  return { text, lossy };
}
